
// stores/gCollectorStore.ts
import { defineStore } from 'pinia'
import { ref, readonly, computed } from 'vue'
import { infoLog, warnLog, errorLog } from '@/composables/useLogger'

declare global {
    interface Window {
        signalRUrl: string
        apiRootUrl: string
        env: string
        token: string
        UserName?: string
        userId?: string
        isAgent: boolean
        tenant?: string
        tenantId?: string
        email?: string
        firstName?: string
        surname?: string
        loginToken?: string
        sessionTimeout?: string
        Affiliates?: string
    }
}

function isClient(): boolean {
    return typeof window !== 'undefined'
}

export const gCollectorStore = defineStore('gCollectorStore', () => {

    const _ajaxBase = ref(`${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`);
    // --- state (reactive)
    const _signalRUrl = ref('')
    const _apiRootUrl = ref('')
    const _env = ref('')
    const _token = ref('')
          
    const _userName = ref('')
    const _userId = ref('')
    const _isAgent = ref(false)
    const _tenant = ref('')
    const _tenantId = ref('')
    const _email = ref('')
    const _firstName = ref('')
    const _surname = ref('')
    const _loginToken = ref('')
    const _sessionTimeout = ref('') // raw string from window
    const _sessionTime = ref<number>(0) // derived numeric (e.g., seconds)

    const _affiliates = ref('')
    
    const _initialized = ref(false)

    // --- actions
    function initFromWindow(w: Window = window) {
        if (!isClient()) {
            warnLog?.('gCollectorStore.initFromWindow called on server; skipping')
            return
        }
        try {
            _signalRUrl.value = w.signalRUrl ?? ''
            _apiRootUrl.value = w.apiRootUrl ?? ''
            _env.value = w.env ?? ''
            _token.value = w.token ?? ''
            
            _userName.value = w.UserName ?? ''
            _userId.value = w.userId ?? ''
            _isAgent.value = w.isAgent
            _tenant.value = w.tenant ?? ''
            _tenantId.value = w.tenantId ?? ''
            _email.value = w.email ?? ''
            _firstName.value = w.firstName ?? ''
            _surname.value = w.surname ?? ''
            _loginToken.value = w.loginToken ?? ''
            _sessionTimeout.value = w.sessionTimeout ?? ''
            
            // Example: derive a numeric session time (e.g., minutes -> seconds)
            const parsed = Number(_sessionTimeout.value)
            _sessionTime.value = Number.isFinite(parsed) ? parsed : 0

            _affiliates.value = w.Affiliates ?? '';


            _initialized.value = true
            infoLog?.('gCollectorStore initialized from window')
        } catch (err) {
            errorLog?.('Failed to init gCollectorStore', err)
        }
    }


    // -- public (read-only for consumers)
    const ajaxBase = readonly(_ajaxBase)
    const signalRUrl = readonly(_signalRUrl)
    const apiRootUrl = readonly(_apiRootUrl)
    const env = readonly(_env)
    const token = readonly(_token)
    const userName = readonly(_userName)
    const userId = readonly(_userId)
    const isAgent = readonly(_isAgent)
    const tenant = readonly(_tenant)
    const tenantId = readonly(_tenantId)
    const email = readonly(_email)
    const firstName = readonly(_firstName)
    const surname = readonly(_surname)
    const loginToken = readonly(_loginToken)
    const sessionTimeout = readonly(_sessionTimeout)
    const sessionTime = readonly(_sessionTime)
    const affiliates = readonly(_affiliates)

    const initialized = readonly(_initialized)
    

    return {
        ajaxBase,
        // state
        signalRUrl,
        apiRootUrl,
        env,
        token,
        userName,
        userId,
        isAgent,
        tenant,
        tenantId,
        email,
        firstName,
        surname,
        loginToken,
        sessionTimeout,
        sessionTime,
        initialized,
        affiliates,

        // actions
        initFromWindow,
    }
})
